import React, { useState, useEffect } from "react";

const OptimizedImage = ({ 
  src, 
  alt, 
  className, 
  style, 
  loading = "lazy", 
  placeholder = null,
  onLoad = () => {},
  onError = () => {},
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (loading === "lazy") {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsIntersecting(true);
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );

      const imageElement = document.getElementById(`optimized-image-${src}`);
      if (imageElement) {
        observer.observe(imageElement);
      }

      return () => observer.disconnect();
    } else {
      setIsIntersecting(true);
    }
  }, [src, loading]);

  const handleLoad = (e) => {
    setIsLoaded(true);
    onLoad(e);
  };

  const handleError = (e) => {
    setHasError(true);
    onError(e);
  };

  const shouldLoad = loading === "eager" || isIntersecting;

  return (
    <div
      id={`optimized-image-${src}`}
      className={`optimized-image-container ${className || ''}`}
      style={style}
    >
      {/* Placeholder while loading */}
      {!isLoaded && !hasError && placeholder && (
        <div className="image-placeholder">
          {placeholder}
        </div>
      )}

      {/* Error fallback */}
      {hasError && (
        <div className="image-error" role="img" aria-label={alt}>
          <span>Image failed to load</span>
        </div>
      )}

      {/* Actual image */}
      {shouldLoad && (
        <img
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          loading={loading}
          className={className}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
            display: hasError ? 'none' : 'block'
          }}
          {...props}
        />
      )}
    </div>
  );
};

export default OptimizedImage;